import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ShieldCheck, ArrowRight, Lock, CheckCircle2, FileText } from "lucide-react";

const PAYMENT_METHODS = [
  {
    name: "Bank Wire Transfer (T/T)",
    type: "Telegraphic Transfer",
    desc: "Direct SWIFT / IBAN (USD, EUR, GBP)",
    badge: "Primary B2B",
    icon: "🏦",
    popular: true,
  },
  {
    name: "Western Union",
    type: "Global Wire Remittance",
    desc: "Fast worldwide commercial transfer",
    badge: "Instant / Fast",
    icon: "🟡",
    popular: true,
  },
  {
    name: "MoneyGram",
    type: "International Transfer",
    desc: "Worldwide secure agent & bank transfer",
    badge: "Fast Settlement",
    icon: "🔴",
    popular: true,
  },
  {
    name: "Letter of Credit (L/C)",
    type: "Trade Finance",
    desc: "Irrevocable L/C at Sight (Container Orders)",
    badge: "Bulk & FCL",
    icon: "📜",
    popular: true,
  },
  {
    name: "Ria Money Transfer",
    type: "Global Remittance",
    desc: "Secure international business transfers",
    badge: "Low Fees",
    icon: "🟠",
  },
  {
    name: "Remitly",
    type: "Digital Remittance",
    desc: "Direct bank deposit worldwide",
    badge: "Express Digital",
    icon: "🔵",
  },
  {
    name: "Wise (TransferWise)",
    type: "Multi-Currency Transfer",
    desc: "Real exchange rate B2B wire",
    badge: "Low FX Cost",
    icon: "🟢",
  },
  {
    name: "Payoneer",
    type: "Cross-Border B2B",
    desc: "International commercial invoicing",
    badge: "B2B Billing",
    icon: "🟣",
  },
  {
    name: "PayPal",
    type: "Sample Orders",
    desc: "Express proto samples & tooling fees",
    badge: "Samples Only",
    icon: "💳",
  },
];

const PaymentMethodsCarousel = () => {
  return (
    <section className="relative overflow-hidden border-t border-black/10 bg-brand-smoke py-20 md:py-28" data-testid="payment-methods-section">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-black/10 pb-10">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 bg-brand-ink px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-white">
                <Lock size={11} className="text-brand-red" /> Secure Export Transactions
              </span>
              <span className="font-mono text-[11px] text-neutral-400 uppercase tracking-wider">
                Certified Factory Terms
              </span>
            </div>
            <h2 className="mt-4 font-display text-3xl font-semibold tracking-tighter md:text-4xl lg:text-5xl">
              ACCEPTED PAYMENT METHODS<span className="text-brand-red">.</span>
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-600 sm:text-base">
              Standard Export Payment Terms: <strong className="text-brand-ink">50% advance deposit</strong> upon order confirmation to commence production, and <strong className="text-brand-ink">50% balance before shipment</strong> following final QC inspection and documentation.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/terms"
              data-testid="payment-view-terms"
              className="btn-outline !h-10 !px-4 !text-xs"
            >
              <FileText size={14} /> View Payment Terms
            </Link>
            <Link
              to="/inquiry"
              data-testid="payment-request-quote"
              className="btn-primary !h-10 !px-4 !text-xs"
            >
              Start Order <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Payment Policy Key Badges */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { title: "50% Advance Deposit", desc: "Secures materials & production queue upon order confirmation" },
            { title: "50% Balance on Dispatch", desc: "Payable after video QC inspection & pre-shipment notice" },
            { title: "100% Commercial Security", desc: "Bank-to-bank SWIFT MT103 receipts & BL copies provided" },
            { title: "Sample Orders Protocol", desc: "100% advance via Western Union, Wise, or PayPal for samples" },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 border border-black/10 bg-white p-4">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-brand-red" />
              <div>
                <p className="font-display text-sm font-semibold text-brand-ink">{item.title}</p>
                <p className="mt-0.5 text-xs text-neutral-500 leading-normal">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Infinite Scrolling Marquee Track via react-fast-marquee */}
      <div className="relative mt-12 py-2">
        <Marquee speed={40} gradient={false} pauseOnHover>
          <div className="flex gap-4 px-2">
            {PAYMENT_METHODS.map((m) => (
              <div
                key={m.name}
                className="group flex w-[280px] shrink-0 flex-col justify-between border border-black/10 bg-white p-5 transition-all duration-300 hover:border-brand-ink hover:shadow-md sm:w-[320px]"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl">{m.icon}</span>
                    <span className="border border-black/10 bg-brand-smoke px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-neutral-600 group-hover:border-brand-ink group-hover:bg-brand-ink group-hover:text-white transition-colors">
                      {m.badge}
                    </span>
                  </div>
                  <h4 className="mt-4 font-display text-lg font-semibold tracking-tight text-brand-ink">
                    {m.name}
                  </h4>
                  <p className="font-mono text-[11px] uppercase tracking-wider text-brand-red">
                    {m.type}
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-neutral-500">
                    {m.desc}
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-1.5 border-t border-black/5 pt-3 font-mono text-[10px] text-neutral-400">
                  <ShieldCheck size={12} className="text-green-600" /> Verified Export Channel
                </div>
              </div>
            ))}
          </div>
        </Marquee>
      </div>

      {/* Subtle bottom note */}
      <div className="mx-auto mt-6 max-w-[1400px] px-5 text-center lg:px-10">
        <p className="font-mono text-[11px] text-neutral-400">
          Official bank beneficiary details and remittance invoices are provided directly with your official Proforma Invoice (PI).
        </p>
      </div>
    </section>
  );
};

export default PaymentMethodsCarousel;
